<template>
    <div class="merged-page">
        <AppBreadCrumb :paths="[{ title: $t('message.bannedWords') }]" :select="true">
            <a-input-search
                v-model="query"
                :placeholder="$t('message.startFilter')"
                class="search-input"
                @search="onSearch"
            />
            <a-button
                class="actionButtonBreadcrumb"
                @click="showModalWithoutEdit"
                v-if="checkPermission(PERMISSION_TYPES['bannedWord.create'].tr.name)"
            >
                <md-icon>add</md-icon>
                {{ $t("message.newBannedWord") }}
            </a-button>
        </AppBreadCrumb>

        <div style="background-color: #ffffff" class="d-flex w-100">
            <div class="side-menubar-wrapper" style="width: 15%">
                <ContentsMenu />
            </div>
            <div class="content">
                <div id="bannedWords">
                    <div style="width: 99%">
                        <a-table
                            :key="'vtable-' + renderCount"
                            :columns="cols()"
                            :dataSource="getFilteredRows()"
                            :loading="isBusy"
                            :locale="{
                                emptyText: $t('message.noBannedWordFound'),
                            }"
                            :pagination="false"
                            class="custom-table2"
                            rowKey="_id"
                            @change="tableChange"
                        >
                            <div :key="'statusSlot' + $i18n.locale" :slot="'statusSlot'" slot-scope="data, item">
                                {{ item.isActive ? $t("message.active") : $t("message.passive") }}
                            </div>
                            <div :key="'actionsSlot' + $i18n.locale" :slot="'actionsSlot'" slot-scope="data, item">
                                <div class="d-flex w-100">
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        @click="edit(item._id)"
                                        v-if="checkPermission(PERMISSION_TYPES['bannedWord.update'].tr.name)"
                                    >
                                        <span style="margin-right: 16px; display: block">{{ $t("message.edit") }}</span>
                                        <ActionEdit />
                                    </a-button>
                                    <a-button
                                        class="actionButton d-flex align-items-center"
                                        @click="remove(item._id)"
                                        v-if="checkPermission(PERMISSION_TYPES['bannedWord.delete'].tr.name)"
                                    >
                                        <span style="margin-right: 16px; display: block">{{
                                            $t("message.deleteField")
                                        }}</span>
                                        <ActionDelete />
                                    </a-button>
                                </div>
                            </div>
                        </a-table>
                        <div class="mt-3 d-flex justify-content-end">
                            <Pagination
                                :page-change="pageChange"
                                :page-sizes="pageSizes"
                                :total="bannedWords.total"
                                v-if="initialized"
                                :default-current-page="page"
                                :default-page-size="perPage"
                            />
                        </div>
                    </div>
                    <div class="fab d-flex justify-content-center align-items-center" @click="showModalWithoutEdit">
                        <md-icon class="">add</md-icon>
                    </div>
                    <Modal2
                        v-if="isModalShow"
                        id="banned-word-form"
                        :title="editMode ? $t('message.updateBannedWordForm') : $t('message.newBannedWordForm')"
                        :show-footer="false"
                        @cancel="hideModal"
                    >
                        <a-alert
                            v-if="$validator.errors.items.length"
                            :message="$t('message.error')"
                            class="mb-2"
                            showIcon
                            type="error"
                        >
                            <div slot="description">
                                <div v-for="err of $validator.errors.items">
                                    <span>
                                        {{ err.msg }}
                                    </span>
                                </div>
                            </div>
                        </a-alert>
                        <a-form class="d-flex flex-column justify-content-center" @submit.prevent="handleSave">
                            <a-row>
                                <a-col>
                                    <a-form-item
                                        id="name-input-group"
                                        :label="$t('message.word')"
                                        label-for="name-input"
                                    >
                                        <a-input
                                            id="name-input"
                                            v-model="bannedWord.content"
                                            :name="$t('message.word')"
                                            :placeholder="$t('message.bannedWordAndWordGroup')"
                                            autocomplete="off"
                                            trim
                                            type="text"
                                        />
                                        <!--<span class="text-danger mt-3">{{ errors.first('name') }}</span>-->
                                    </a-form-item>
                                </a-col>
                                <a-col>
                                    <a-form-item :label="$t('message.status')">
                                        <a-radio-group v-model="bannedWord.isActive" name="status" required>
                                            <a-radio :value="true">
                                                {{ $t("message.active") }}
                                            </a-radio>
                                            <a-radio :value="false" id="pasv_button">
                                                {{ $t("message.passive") }}
                                            </a-radio>
                                        </a-radio-group>
                                    </a-form-item>
                                </a-col>
                            </a-row>
                        </a-form>
                        <a-row>
                            <a-col id="buttons">
                                <a-button @click="hideModal">
                                    <template>
                                        {{ $t("message.cancel") }}
                                    </template>
                                </a-button>

                                <a-button
                                    :disabled="loading"
                                    type="submit"
                                    variant="primary"
                                    class="btn-primary"
                                    @click="handleSave"
                                >
                                    <template v-if="!loading">
                                        <span v-if="!editMode">{{ $t("message.save") }}</span>
                                        <span v-else>{{ $t("message.update") }}</span>
                                    </template>
                                    <template v-else>
                                        <font-awesome-icon icon="spinner" spin />
                                    </template>
                                </a-button>
                            </a-col>
                        </a-row>
                    </Modal2>
                    <ChatBar v-if="!isAdmin" :visible="false" />
                </div>
            </div>
        </div>
        <ChatBar v-if="!isAdmin" :visible="false" />
    </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { library } from "@fortawesome/fontawesome-svg-core";
import { faPencilAlt, faPlusCircle, faSpinner, faTimes } from "@fortawesome/free-solid-svg-icons";
import AppBreadCrumb from "../../components/common/AppBreadCrumb";
import * as _ from "lodash";
import { log2 } from "../../utils/log2";
import ChatBar from "../../components/ChatBar";
import ContentsMenu from "./ContentsMenu";
import { tableSorter } from "../../utils/utils";
import { sendSocketMessage } from "../../utils/socketStream";
import ActionDelete from "../../components/svg/ActionDelete";
import ActionEdit from "../../components/svg/ActionEdit";
import Pagination from "../../components/Pagination";
import Modal2 from "../../components/common/Modal2.vue";
import { PERMISSION_TYPES } from "../../language/permissionTypes";

library.add(faPencilAlt, faTimes, faPlusCircle, faSpinner);

const bannedWordInitialObject = {
    _id: null,
    content: "",
    isActive: true,
};
const log = log2("BannedWords");
export default {
    name: "BannedWords",
    components: {
        AppBreadCrumb,
        ChatBar,
        ActionDelete,
        ActionEdit,
        Pagination,
        ContentsMenu,
        Modal2,
    },
    data() {
        return {
            isBusy: true,
            loading: false,
            editMode: false,
            bannedWord: {
                ...bannedWordInitialObject,
            },
            pageSizes: ["10", "25", "50", "100"],
            query: this.$route.query.query || null,
            cols: () => [
                {
                    dataIndex: "content",
                    title: this.$t("message.word"),
                    ...tableSorter("content", "string", this.currentSort),
                },
                {
                    dataIndex: "isActive",
                    title: this.$t("message.status"),
                    scopedSlots: { customRender: "statusSlot" },
                    ...tableSorter("isActive", "string", this.currentSort),
                },
                {
                    title: this.$t("message.transactions"),
                    scopedSlots: { customRender: "actionsSlot" },
                    width: "10%",
                },
            ],
            perPage: parseInt(this.$route.query.perPage || 10),
            page: parseInt(this.$route.query.page || 1),
            renderCount: 0,
            currentSort: {
                columnKey: "isActive",
                order: "ascend",
            },
            isModalShow: false,
            initialized: false,
            queryAction: this.$route.query.action,
        };
    },
    computed: {
        PERMISSION_TYPES() {
            return PERMISSION_TYPES;
        },
        ...mapState({
            bannedWords: (state) => state.bannedWord.bannedWords,
            isAdmin: (state) => state.auth.adminUser != null,
            site: (state) => state.site.site,
        }),
        ...mapGetters({
            undeleted: "bannedWord/getUndeleted",
            isDarkMode: "user/isDarkMode",
            checkPermission: "auth/checkPermission",
        }),
    },
    methods: {
        ...mapActions({
            getBannedWords: "bannedWord/getBannedWords",
            getBannedWord: "bannedWord/getBannedWord",
            createBannedWord: "bannedWord/createBannedWord",
            updateBannedWord: "bannedWord/updateBannedWord",
            deleteBannedWord: "bannedWord/deleteBannedWord",
        }),
        showModal() {
            this.isModalShow = true;
        },
        hideModal() {
            this.isModalShow = false;
            this.reset();
        },
        showModalWithoutEdit() {
            this.editMode = false;
            this.showModal();
        },
        tableChange(pagination, filters, sorter) {
            log("tableChange", { pagination, filters, sorter });
            this.perPage = pagination.pageSize;
            log("tableChange", { pagination, filters, sorter });
            const newSort = {
                columnKey: sorter.columnKey,
                order: sorter.order,
            };
            const oldSort = this.currentSort;
            log("show me current Sort", {
                currentSort: this.currentSort,
                newSort,
            });
            if (!_.isEqual(newSort, oldSort)) {
                this.currentSort = newSort;
                this.renderCount += 1;
                this.$forceUpdate();
            }
        },

        getFilteredRows() {
            return this.query
                ? this.undeleted.filter((x) => x.content.toLowerCase().includes(this.query))
                : this.undeleted;
        },
        onSearch(value) {
            log("onSearch", value);
            this.query = value;
            this.updateQueryParams("query", value);
        },
        pageChange(page, pageSize) {
            log("pageChange", { page, pageSize });
            this.page = page;
            this.perPage = pageSize;
        },
        onShowSizeChange(current, pageSize) {
            log("onShowSizeChange", current, pageSize);
            this.page = current;
            this.perPage = pageSize;
        },
        get() {
            this.isBusy = true;
            this.getBannedWords({
                perPage: this.perPage,
                page: this.page,
            }).finally(() => {
                this.isBusy = false;
                this.initialized = true;
                if (this.queryAction == "create") this.isModalShow = true;
            });
        },
        edit(id) {
            this.getBannedWord(id).then((response) => {
                this.editMode = true;
                this.bannedWord = _.cloneDeep(response);
                /*this.$bvModal.show("banned-word-form");*/
                this.showModal();
            });
        },
        remove(id) {
            this.$dialog
                .confirm(this.$t("message.doYouWishToDeleteBannedWord"), {
                    okText: this.$t("message.continue"),
                    cancelText: this.$t("message.cancel"),
                })
                .then(() => {
                    this.deleteBannedWord(id).then((res) => {
                        sendSocketMessage("user banned-word-deleted", {
                            wordId: id,
                            siteId: this.site._id,
                        });
                    });
                });
        },
        reset() {
            this.bannedWord = {
                ...bannedWordInitialObject,
            };
        },
        handleSave() {
            this.$validator.validateAll().then((response) => {
                this.loading = true;

                if (response) {
                    const result = this.bannedWord._id
                        ? this.updateBannedWord(this.bannedWord).then(() => {
                              sendSocketMessage("user banned-word-updated", {
                                  siteId: this.site._id,
                              });
                          })
                        : this.createBannedWord(this.bannedWord).then(() => {
                              sendSocketMessage("user banned-word-added", {
                                  siteId: this.site._id,
                              });
                          });

                    result
                        .then(() => {
                            this.$toasted.show(this.$t("message.bannedWordSaved"), {
                                duration: 3000,
                                position: "bottom-center",
                            });
                            /*this.$bvModal.hide("banned-word-form");*/
                            this.hideModal();
                        })
                        .catch((err) => {
                            log.error(err.response, err.message);
                            const errMsg =
                                err.response && err.response.data && err.response.data.localizedMessages
                                    ? err.response.data.localizedMessages[this.$i18n.locale]
                                    : err.message;
                            this.$toasted.error(errMsg, {
                                duration: 4000,
                                position: "bottom-center",
                            });
                        })
                        .finally(() => (this.loading = false));
                } else {
                    this.loading = false;
                }
            });
        },
        updateQueryParams(key, value) {
            if (value === "" || value == null) {
                let query = Object.assign({}, this.$route.query);
                delete query[key];
                this.$router.replace({ query });
            } else {
                let { query } = this.$route;
                query = { ...query, [key]: encodeURI(value) };
                this.$router.replace({ query });
            }
        },
    },

    mounted() {
        log("mounted page", this.page, this.$route.query.page);
        this.get();
        this.updateQueryParams();
    },
    updated() {
        if (this.$validator.locale !== this.$i18n.locale) this.$validator.localize(this.$i18n.locale);
    },
    watch: {
        page(newValue, oldValue) {
            if (newValue) {
                this.get();
                this.updateQueryParams("page", newValue);
            }
        },
        perPage(newValue, oldValue) {
            if (newValue) {
                this.get();
                this.updateQueryParams("perPage", newValue);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.content {
    width: 85%;
    background: #eef0f8;
}

#bannedWords {
    min-height: 90vh;
}

.merged-page {
    position: relative;

    .custom-breadcrumb {
        margin-bottom: 1px;
    }
}

.custom-tabs .nav.nav-tabs {
    position: fixed;
    top: 92px;
    left: 92px;
    right: 30px;
    z-index: 123;
}

.custom-breadcrumb {
    margin-bottom: 1px;
}

.new-theme {
    #bannedWords {
        .actionButton {
            border: none;
            box-shadow: none;
        }
    }
}

.fab {
    width: 50px;
    height: 50px;
    border-radius: 25px;
    background-color: white;
    position: absolute;
    bottom: 2%;
    left: 16%;
    z-index: 9999;
    transition: all 0.3s;
    box-shadow: 0 0 0 0 rgba(100, 100, 100, 1);
    transform: scale(1);
    animation: pulse 3s infinite;
    cursor: pointer;
}

@keyframes pulse {
    0% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 rgba(100, 100, 100, 0.5);
    }

    70% {
        transform: scale(1);
        box-shadow: 0 0 0 10px #00000000;
    }

    100% {
        transform: scale(0.95);
        box-shadow: 0 0 0 0 #00000000;
    }
}

#buttons {
    display: flex;
    justify-content: flex-end;
    gap: 7px;
}
</style>

<style lang="scss">
.new-theme {
    #bannedWords {
        .search-input {
            width: auto;
            margin-right: 8px;

            input {
                height: 26px;
                background-color: #f3f6f9;
                border-radius: 6px;
                border: none;

                color: #3699ff;
                font-family: Poppins;
                font-size: 10px;
                font-weight: 700;

                &::placeholder {
                    color: #3699ff;
                    font-family: Poppins;
                    font-size: 10px;
                    font-weight: 700;
                }
            }
        }
    }
}

#banned-word-form {
    .ant-form-item label {
        font-weight: 700;
        font-size: 15px;
        color: #9ba2b6;
        line-height: 0px;
    }

    .ant-form legend {
        color: rgba(0, 0, 0, 0.45);
        border-bottom: none;
    }

    .ant-form-item-label {
        line-height: 28px;
    }

    .ant-form-item-label > label::after {
        content: "";
    }

    .ant-form-item {
    }

    .ant-radio-wrapper {
        color: #4b5361 !important;
        font-weight: 400 !important;
        height: 14px;
        width: 14px;
        display: inline-flex;
        align-items: center;
        justify-content: space-between;
    }

    .ant-radio-wrapper:last-child {
        margin-left: 50px;
    }

    .ant-radio-wrapper span {
        font-size: 14px;
    }

    .modal-body button {
        width: 160px;
    }

    .btn-primary {
        background-color: #03a9f4;
        border-color: #03a9f4;
        color: #fff;
    }

    .btn-primary:hover {
        background-color: #028fce;
        border-color: #028fce;
        color: #fff;
    }

    .btn-secondary {
        background-color: #6c757d;
        border-color: #6c757d;
        color: #fff;
    }

    .btn-secondary:hover {
        background-color: #596267;
        border-color: #596267;
        color: #fff;
    }
}
</style>
